/**
 * NotificationToast Component - Phase 5 Epic 5.4
 * Displays real-time toast notifications for incoming notifications
 */

import React, { useEffect } from 'react';
import { useWebSocketContext } from '@/contexts/WebSocketContext';
import toast from 'react-hot-toast';
import { Bell, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';

export type NotificationType =
  | 'agent_execution'
  | 'campaign_update'
  | 'milestone_reached'
  | 'mention'
  | 'system_alert'
  | 'error_alert'
  | 'approval_required'
  | 'recommendation';

export type NotificationPriority = 'critical' | 'high' | 'medium' | 'low';

interface NotificationToastProps {
  userId?: string;
  enabled?: boolean;
  playSound?: boolean;
  showOnlyPriorities?: NotificationPriority[];
  excludeTypes?: NotificationType[];
}

export function NotificationToast({
  userId = 'anonymous',
  enabled = true,
  playSound = false,
  showOnlyPriorities,
  excludeTypes = [],
}: NotificationToastProps) {
  const { socket, isConnected } = useWebSocketContext();

  useEffect(() => {
    if (!socket || !isConnected || !enabled) return;

    const handleNewNotification = (data: any) => {

      // Filter by priority if specified
      if (
        showOnlyPriorities &&
        showOnlyPriorities.length > 0 &&
        !showOnlyPriorities.includes(data.priority)
      ) {
        return;
      }

      // Filter by type if specified
      if (excludeTypes.includes(data.type)) {
        return;
      }

      // Play sound if enabled
      if (playSound) {
        playNotificationSound(data.priority);
      }

      // Get icon emoji
      const icon = getNotificationEmoji(data.type, data.priority);

      // Show toast based on priority
      if (data.priority === 'critical' || data.priority === 'high') {
        toast.error(
          (t) => (
            <div className="flex flex-col gap-1">
              <div className="font-semibold">{data.title}</div>
              <div className="text-sm">{data.message}</div>
              {data.actionUrl && (
                <button
                  className="mt-2 text-xs underline"
                  onClick={() => {
                    window.location.href = data.actionUrl;
                    toast.dismiss(t.id);
                  }}
                >
                  View Details
                </button>
              )}
            </div>
          ),
          {
            icon: icon,
            duration: getDuration(data.priority),
          }
        );
      } else {
        toast(
          (t) => (
            <div className="flex flex-col gap-1">
              <div className="font-semibold">{data.title}</div>
              <div className="text-sm">{data.message}</div>
              {data.actionUrl && (
                <button
                  className="mt-2 text-xs underline"
                  onClick={() => {
                    window.location.href = data.actionUrl;
                    toast.dismiss(t.id);
                  }}
                >
                  View Details
                </button>
              )}
            </div>
          ),
          {
            icon: icon,
            duration: getDuration(data.priority),
          }
        );
      }
    };

    socket.on('notification:new', handleNewNotification);

    return () => {
      socket.off('notification:new', handleNewNotification);
    };
  }, [socket, isConnected, enabled, playSound, showOnlyPriorities, excludeTypes]);

  return null; // This is a behavior-only component
}

// Helper functions

function getNotificationEmoji(type: NotificationType, priority: NotificationPriority): string {
  // Priority takes precedence
  if (priority === 'critical') return '🚨';
  if (priority === 'high') return '⚠️';

  // Then check type
  switch (type) {
    case 'error_alert':
      return '❌';
    case 'milestone_reached':
      return '🎯';
    case 'mention':
      return '@';
    case 'recommendation':
      return '💡';
    case 'agent_execution':
      return '🤖';
    case 'campaign_update':
      return '📢';
    case 'system_alert':
      return '⚙️';
    case 'approval_required':
      return '✋';
    default:
      return '🔔';
  }
}

function getDuration(priority: NotificationPriority): number {
  switch (priority) {
    case 'critical':
      return 10000; // 10 seconds
    case 'high':
      return 7000; // 7 seconds
    case 'medium':
      return 5000; // 5 seconds
    case 'low':
      return 3000; // 3 seconds
    default:
      return 5000;
  }
}

function playNotificationSound(priority: NotificationPriority) {
  // Create audio context
  const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  // Configure sound based on priority
  const frequency = priority === 'critical' ? 800 : priority === 'high' ? 600 : 400;
  const duration = priority === 'critical' ? 200 : 100;

  oscillator.frequency.value = frequency;
  oscillator.type = 'sine';
  gainNode.gain.value = 0.1;

  oscillator.start();
  oscillator.stop(audioContext.currentTime + duration / 1000);
}

// Hook for easy integration
export function useNotificationToast(options?: NotificationToastProps) {
  return <NotificationToast {...options} />;
}
